import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ArrowRight, ChevronRight } from 'lucide-react';
import { services, serviceCategories } from '../data/services';

export const Footer = ({ onOpenLeadPopup }) => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Our Attorneys', path: '/attorneys' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  return (
    <footer className="bg-[#0A2A5E] text-white" data-testid="main-footer">
      {/* Main Mega Footer */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12">

          {/* About Column */}
          <div className="lg:w-[240px] flex-shrink-0">
            <div className="flex items-center mb-4">
              <img src="/logo-footer.png" alt="Legal Emphasis" className="h-12" />
            </div>
            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
              Protecting your brands, inventions, and creative assets. Trusted IP counsel for businesses nationwide.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-300 text-sm">
                  One Sansome Street, Ste 1400,<br />
                  San Francisco, CA 94104
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gray-400" />
                <a href="tel:+18887505204" className="text-gray-300 text-sm hover:text-white transition-colors">
                  +1 (888) 750-5204
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400" />
                <a href="mailto:info@legalemphasis.com" className="text-gray-300 text-sm hover:text-white transition-colors">
                  info@legalemphasis.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-gray-400" />
                <span className="text-gray-300 text-sm">Mon - Fri: 9 AM - 6 PM</span>
              </div>
            </div>
          </div>

          {/* Quick Links + Legal */}
          <div className="flex-shrink-0">
            <h4 className="text-sm font-semibold mb-6 text-white uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="text-sm font-semibold mt-8 mb-4 text-white uppercase tracking-wider">Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services by Category — flex auto-sizes each column */}
          {serviceCategories.map((category) => (
            <div key={category.name} className="flex-shrink-0">
              <h4 className="text-sm font-semibold mb-6 text-white uppercase tracking-wider whitespace-nowrap">
                {category.name === 'Intellectual Property' ? 'IP Services' : category.name}
              </h4>
              <ul className="space-y-3">
                {category.services.map((service) => (
                  <li key={service.id}>
                    <Link 
                      to={`/services/${service.slug}`}
                      className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-2 group whitespace-nowrap"
                      data-testid={`footer-service-${service.slug}`}
                    >
                      <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      {service.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="le-container py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className="text-gray-400 text-xs">
              &copy; {new Date().getFullYear()} Legal Emphasis. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-bottom-privacy">Privacy Policy</Link>
              <span className="text-gray-600">|</span>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-bottom-terms">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
